import { theme } from '@theme';
import { hasRole } from '@utils/HelperFunctions';
import { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router';
import { AppState } from '../../../../store/AppState';
import { CurrentUser } from '../../../../types';
import { PortfolioResource } from '../../../../types/PortfolioResource';
import { Icon, Typography } from '../../../style';
import ExportList from '../bulk-actions/export-list/ExportList';
import PrintOffer from '../bulk-actions/print-offer/PrintOffer';
import MailerStatus from './mailer-status/MailerStatus';
import MarkInactive from './mark-inactive/MarkInactive';
import ReassignDeals from './reassign-deals/ReassignDeals';
import SendLeadsToCRM from './send-leads-to-crm/SendLeadsToCRM';
import UpdateReplacement from './update-replacement/UpdateReplacement';

type PortfolioBulkActionsProps = {
  isVisible: boolean;
  selectedRecords: PortfolioResource[];
  totalRecordsCount: number;
  isAllRowsSelected: boolean;
  dealType: string;
  toggleSelectedRows: (value: boolean) => void;
};

const PortfolioBulkActions: FC<PortfolioBulkActionsProps> = (props) => {
  const [isClearSelectionLink, setIsClearSelectionLink] = useState<boolean>(false);
  const linkText =
    isClearSelectionLink && props.isAllRowsSelected
      ? 'Clear Selection'
      : 'Select all filtered deals';
  const selectedRecordsCount = props.selectedRecords.length;
  const { permissions } = useSelector<AppState, CurrentUser>((state) => state.currentUser);

  useEffect(() => {
    if (isClearSelectionLink && !props.isVisible) {
      setIsClearSelectionLink(false);
    }
  }, [isClearSelectionLink, props.isVisible]);

  const onSelectAllFiltersClick = () => {
    setIsClearSelectionLink(true);
    props.toggleSelectedRows(true);
  };

  const onCloseButtonClick = (): void => {
    props.toggleSelectedRows(false);
  };

  const getPortfolioBulkActionsBody = (): JSX.Element => {
    return (
      <div className='bulk-actions-body'>
        {hasRole(permissions, 'changeAssignee') && (
          <ReassignDeals
            isVisible={props.isVisible}
            selectedRecords={props.selectedRecords}
            selectedRecordsCount={selectedRecordsCount}
            isAllRowsSelected={props.isAllRowsSelected}
            isClearSelectionLink={isClearSelectionLink}
            toggleSelectedRows={props.toggleSelectedRows}
          />
        )}

        {hasRole(permissions, 'changeDealStatus') && (
          <MarkInactive
            selectedRecords={props.selectedRecords}
            selectedRecordsCount={selectedRecordsCount}
            toggleSelectedRows={props.toggleSelectedRows}
            isClearSelectionLink={isClearSelectionLink}
            isAllRowsSelected={props.isAllRowsSelected}
            totalRecordsCount={props.totalRecordsCount}
          />
        )}

        {hasRole(permissions, 'markMailers') && (
          <MailerStatus
            isAllRowsSelected={props.isAllRowsSelected}
            isClearSelectionLink={isClearSelectionLink}
            selectedRecords={props.selectedRecords}
            selectedRecordsCount={selectedRecordsCount}
            totalRecordsCount={props.totalRecordsCount}
            toggleSelectedRows={props.toggleSelectedRows}
          />
        )}

        {hasRole(permissions, 'userCanSaveReplacementChromeId') && (
          <UpdateReplacement
            isAllRowsSelected={props.isAllRowsSelected}
            isClearSelectionLink={isClearSelectionLink}
            selectedRecords={props.selectedRecords}
            dealType={props.dealType}
            onCloseButtonClick={onCloseButtonClick}
          />
        )}

        {hasRole(permissions, 'pushLeadsInABulk') && (
          <SendLeadsToCRM
            selectedRecords={props.selectedRecords}
            selectedRecordsCount={selectedRecordsCount}
            toggleSelectedRows={props.toggleSelectedRows}
            totalRecordsCount={props.totalRecordsCount}
            isAllRowsSelected={props.isAllRowsSelected}
            isClearSelectionLink={isClearSelectionLink}
          />
        )}
        <PrintOffer
          selectedRecords={props.selectedRecords}
          selectedRecordsCount={selectedRecordsCount}
          toggleSelectedRows={props.toggleSelectedRows}
          isClearSelectionLink={isClearSelectionLink}
          isAllRowsSelected={props.isAllRowsSelected}
          totalRecordsCount={props.totalRecordsCount}
        />
        {hasRole(permissions, 'reportingDataExport') && (
          <ExportList
            selectedRecords={props.selectedRecords}
            selectedRecordsCount={selectedRecordsCount}
            toggleSelectedRows={props.toggleSelectedRows}
            isClearSelectionLink={isClearSelectionLink}
            isAllRowsSelected={props.isAllRowsSelected}
            totalRecordsCount={props.totalRecordsCount}
          />
        )}
      </div>
    );
  };

  return (
    <div
      className={` ${props.isVisible ? 'bulk-container-open' : 'bulk-container-close'}`}
      data-testid='bulk-actions-container'
    >
      <div
        className='bulk-action-container portfolio-bulk-action-container'
        data-testid='portfolio-bulk-action-container'
      >
        <div className='bulk-action row-fluid portfolio-bulk-action'>
          <Typography size={19} color={theme.palette.white} marginLeft={5}>
            Bulk Actions
          </Typography>
          <Icon
            size={18}
            type='x'
            color={theme.palette.white}
            marginRight={10}
            onClick={onCloseButtonClick}
            className='bulk-actions-close'
            data-testid='close-icon'
          />
        </div>
        <div className='bulk-action row-fluid portfolio-bulk-action'>
          <Typography
            size={theme.typography.fontSize}
            color={theme.palette.white}
            marginLeft={5}
            marginRight={15}
            display='inline-block'
          >
            These changes are for{' '}
            {isClearSelectionLink && props.isAllRowsSelected
              ? props.totalRecordsCount
              : selectedRecordsCount}{' '}
            customers.
          </Typography>
          <br />
          <Link
            to='#'
            onClick={
              isClearSelectionLink && props.isAllRowsSelected
                ? onCloseButtonClick
                : onSelectAllFiltersClick
            }
            className='bulk-actions-link'
            data-testid='portfolio-bulk-action-link'
          >
            {linkText}
          </Link>
        </div>
        <div className='bulk-action row-fluid action-item portfolio-bulk-action'>
          {getPortfolioBulkActionsBody()}
        </div>
      </div>
    </div>
  );
};

export default PortfolioBulkActions;
